import { Alert, Button, Spinner } from 'react-bootstrap';

import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import CorporatesLogo from '../../../assets/Corporates.svg';
import { getDgiWS1Info, getDgiWS2Info } from '../../../services/dgi.service';
import {
  AddressTaxesValues,
  BusinessCategory,
  CommerceAddressState,
  TaxPayer,
} from '../../../types/add_commerce.types';
import { processDgiResponses, sanitizeRut, validateRutFormat } from '../../../utils/dgiHelpers';

type DgiStatus =
  | { status: 'loading' }
  | { status: 'success'; message: string }
  | { status: 'error'; message: string };

function DgiSection({
  localState,
  businessCategories,
  taxPayers,
  fetchBusinessTypes,
}: {
  localState: CommerceAddressState;
  businessCategories: BusinessCategory[];
  taxPayers: TaxPayer[];
  fetchBusinessTypes: (businessCategoryId: number) => Promise<void>;
}) {
  const { setFieldValue } = useFormikContext<AddressTaxesValues>();
  const [dgiStatus, setDgiStatus] = useState<DgiStatus | null>(null);

  useEffect(() => {
    if (localState?.rut && dgiStatus === null && businessCategories.length > 0 && taxPayers.length > 0) {
      fetchDgiData(localState.rut.toString());
    }
  }, [localState?.rut, dgiStatus, businessCategories, taxPayers]);

  const handleRefreshDgiData = () => {
    if (!localState?.rut) return;
    fetchDgiData(localState.rut.toString());
  };

  const fetchDgiData = async (rut: string) => {
    const sanitizedRut = sanitizeRut(rut);

    if (!validateRutFormat(sanitizedRut)) {
      setDgiStatus({ status: 'error', message: 'Formato de RUT inválido. Debe contener 12 dígitos.' });
      return;
    }

    setDgiStatus({ status: 'loading' });

    try {
      const [ws1Response, ws2Response] = await Promise.all([
        getDgiWS1Info(sanitizedRut),
        getDgiWS2Info(sanitizedRut),
      ]);

      const { successMessages, hasErrors } = await processDgiResponses(
        ws1Response,
        ws2Response,
        businessCategories,
        taxPayers,
        setFieldValue,
        fetchBusinessTypes,
      );

      if (successMessages.length > 0) {
        setDgiStatus({ status: 'success', message: successMessages.join('. ') });
      }

      if (hasErrors && successMessages.length === 0) {
        setDgiStatus({
          status: 'error',
          message:
            'No se pudo obtener información completa desde DGI. Puede completar los campos manualmente.',
        });
      }
    } catch (error) {
      setDgiStatus({
        status: 'error',
        message: 'Error al consultar DGI. Puede completar los campos manualmente.',
      });
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center my-3">
        <h1 className="form-title my-0">
          <img src={CorporatesLogo} alt="Comercio" className="me-3" /> Rubro
        </h1>
        {localState?.rut && dgiStatus !== null && (
          <Button
            type="button"
            className="btn share-button mt-0"
            onClick={() => handleRefreshDgiData()}
            disabled={dgiStatus?.status === 'loading'}
          >
            {dgiStatus?.status === 'loading' ? (
              <>
                <Spinner size="sm" className="me-2" />
                Consultando DGI...
              </>
            ) : (
              'Completar desde DGI'
            )}
          </Button>
        )}
      </div>

      {dgiStatus !== null && dgiStatus?.status !== 'loading' && (
        <Alert variant={dgiStatus?.status === 'error' ? 'warning' : 'success'} className="mb-3">
          <small>{dgiStatus.message}</small>
        </Alert>
      )}
    </>
  );
}

export default DgiSection;
